import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmtINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function SalesOrders() {
  const [sos, setSos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  useEffect(() => {
    setLoading(true);
    api.get("/so")
      .then(({ data }) => setSos(data))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(sos.length / PAGE_SIZE) || 1;
  const paginatedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sos.slice(start, start + PAGE_SIZE);
  }, [sos, page]);

  return (
    <div className="space-y-6" data-testid="so-list-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Sales Orders</div>
        <h1 className="mt-1 text-3xl font-medium tracking-tight" style={{ fontFamily: "'General Sans', system-ui, sans-serif" }}>Sales Orders</h1>
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 whitespace-nowrap">SO Number</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Linked PO</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Dealer</th>
              <th className="text-right px-4 py-3 whitespace-nowrap">Total</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
              <th className="text-left px-4 py-3 whitespace-nowrap">Raised</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <tr key={i} className="animate-pulse">
                  <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-36" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20 ml-auto" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-5 w-20" /></td>
                  <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                </tr>
              ))
            ) : sos.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-16 text-slate-500">No sales orders yet.</td></tr>
            ) : (
              paginatedItems.map(s => (
                <tr key={s.so_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{s.so_number}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-mono text-xs"><Link to={`/po/${s.po_id}`} className="text-blue-700 hover:underline" data-testid={`so-po-${s.po_number}`}>{s.po_number}</Link></td>
                  <td className="px-4 py-3 whitespace-nowrap">{s.dealer_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">{fmtINR(s.total)}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><Badge variant="outline" className={s.status === "fulfilled" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-blue-100 text-blue-800 border-blue-200"}>{s.status}</Badge></td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">{new Date(s.created_at).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        {sos.length > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> to <span className="font-medium">{Math.min(page * PAGE_SIZE, sos.length)}</span> of <span className="font-medium">{sos.length}</span> SOs (100 per page)
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="h-8 text-xs">Previous</Button>
              <span className="text-xs text-slate-600 px-2 font-medium">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 text-xs">Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
